import { useCallback, useRef, useState } from 'react';
import { Add } from '@mui/icons-material';
import type { FinancialStatementSmall } from 'api';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
  SecondaryNavigationSelector,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { useZoom } from 'context/ZoomContext';
import { toString } from 'utilities';

const FINANCIALS_VERSION_SELECTOR_TEST_ID = 'secondary-navigation__selector-financials-version-name';
const FINANCIALS_SAVE_AS_NEW_VERSION_TEST_ID = 'secondary-navigation__selector-financials-save-as-new-version';
const FINANCIALS_VERSION_MENU_VISIBLE_ITEM_LIMIT = 8;
const FINANCIALS_VERSION_SELECTOR_MAX_WIDTH = 320;

interface FinancialsVersionSelectorProps {
  disabled?: boolean;
  onCreateVersion?: () => void;
  onVersionChange: (versionId: string) => void;
  selectedVersion: FinancialStatementSmall | undefined;
  versions: FinancialStatementSmall[];
}

const FinancialsVersionSelector = ({
  disabled,
  onCreateVersion,
  onVersionChange,
  selectedVersion,
  versions,
}: FinancialsVersionSelectorProps) => {
  const { zoomLevel } = useZoom();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [positiveFeedbackKey, setPositiveFeedbackKey] = useState(0);
  const selectedVersionId = toString(selectedVersion?.id);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    canShowFewerOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions: visibleVersions,
  } = useSearchableLimitedOptions({
    getSearchText: version => version.name,
    options: versions,
    visibleLimit: FINANCIALS_VERSION_MENU_VISIBLE_ITEM_LIMIT,
  });

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  const handleSelect = useCallback(
    (versionId: string) => {
      handleClose();
      if (versionId !== selectedVersionId) {
        setPositiveFeedbackKey(key => key + 1);
      }
      onVersionChange(versionId);
    },
    [handleClose, onVersionChange, selectedVersionId]
  );

  const handleCreateVersion = useCallback(() => {
    handleClose();
    onCreateVersion?.();
  }, [handleClose, onCreateVersion]);

  return (
    <>
      <SecondaryNavigationSelector
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="menu"
        data-testid={FINANCIALS_VERSION_SELECTOR_TEST_ID}
        disabled={disabled}
        label="Financials Version"
        onClick={handleOpen}
        positiveFeedbackKey={positiveFeedbackKey}
        sx={{ maxWidth: FINANCIALS_VERSION_SELECTOR_MAX_WIDTH }}
        value={selectedVersion?.name ?? ''}
      />
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        disableAutoFocusItem
        horizontalPlacement="right"
        menuMaxWidth={320}
        menuMinWidth={160}
        menuWidth="content"
        onClose={handleClose}
        open={Boolean(anchorEl)}
        scrollHintItemLabelPlural="versions"
        scrollHintItemLabelSingular="version"
        zoomLevel={zoomLevel}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{ disableListWrap: true }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear financial version search"
          fullWidth
          inputRef={searchInputRef}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Find a Version"
          value={searchValue}
        />
        {visibleVersions.map(version => (
          <PrimaryNavigationMenuItem
            data-testid={`secondary-navigation__selector-financials-version-option-${toString(version.id)}`}
            isActive={toString(version.id) === selectedVersionId}
            key={version.id}
            onClick={() => handleSelect(toString(version.id))}>
            {version.name}
          </PrimaryNavigationMenuItem>
        ))}
        {visibleVersions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            No versions found
          </PrimaryNavigationMenuItem>
        )}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural="versions"
            itemLabelSingular="version"
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural="versions"
            itemLabelSingular="version"
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {onCreateVersion && (
          <PrimaryNavigationMenuAction
            data-testid={FINANCIALS_SAVE_AS_NEW_VERSION_TEST_ID}
            icon={<Add />}
            onClick={handleCreateVersion}>
            Save as New Version
          </PrimaryNavigationMenuAction>
        )}
      </PrimaryNavigationMenu>
    </>
  );
};

export default FinancialsVersionSelector;
